import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataEventType, SourceDataFinding, SourceDataFindingStatus, SourceDataItem, SourceDataItemGroupType, Trial } from "@/api/dtos";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, Chip, List, Paper, Table, TableCell, TableHead, TableRow, Theme, Tooltip, Typography, buttonClasses, styled, useTheme } from "@mui/material";
import { immerable } from "immer";
import { FC, memo, useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { TableVirtuoso, Virtuoso } from "react-virtuoso";
import SourceDataFindingDetails from "./SourceDataFindingDetails";
import SourceDataFindingView from "./SourceDataFindingView";
import SourceDataFindingsListSort, { DefaultSortableFields, ISourceDataFindingsListSortable, SourceDataFindingsListFilter } from "./SourceDataFindingsListSort";
import ListTableToggle from "../common/ListTableToggle";
import ListViewType from "@/types/list/ListViewType";
import { StatusListItemTooltip } from "../status/StatusListStats";
import sourceDataFindingStatusToColor from "@/utilities/configuration/sourceDataFindingStatusToColor";
import sourceDataFindingStatusToText from "@/utilities/configuration/sourceDataFindingStatusToText";
import { DateTime } from "luxon";
import { cp } from "fs";
import sourceDataFindingTypeToText from "@/utilities/configuration/sourceDataFindingTypeToText";
import ExternalUsersContext from "@/contexts/data/ExternalUsersContext";
import { useLocalStorage } from "react-use/lib";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItem from "@/hooks/form/source-data/useItem";


const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));

const FilterSortBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-7),
    alignItems: 'stretch'
}));

const SortBox = styled(Paper)(({ theme }) => ({
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex'
}));

const SearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6)
}));

const SearchButton = styled(Button)(({ theme }) => ({
    minWidth: theme.spacing(6),
    width: theme.spacing(6),
    alignSelf: 'stretch',
    padding: theme.spacing(0)
}));

const CountBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6),
    marginRight: theme.spacing(2)
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    // minWidth: theme.spacing(18.75)
}));

const FindingTable = styled(Table)(({ theme }) => ({
    '& tr > td': {
        verticalAlign: 'top'
    },

    [`& a:not(.${buttonClasses.root})`]: {
        color: theme.palette.text.primary,

        '&:hover': {
            color: theme.palette.text.primary,
        },

        '&:visited': {
            color: theme.palette.text.primary,
        }
    }
}));


interface IVirtuosoContext {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    theme: Theme;
    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
}

interface IVirtuosoInnerItem {
    index: number;
    item: SourceDataFinding;
    context: IVirtuosoContext;
}

interface IVirtuosoTableInnerItem extends IVirtuosoInnerItem {
    hideStatus?: boolean;
    hideTrial?: boolean;
    hideInstitution?: boolean;
    hideParticipant?: boolean;
    hideEvent?: boolean;
    hideForm?: boolean;
    hideItemGroup?: boolean;
    hideItem?: boolean;
    hideType?: boolean;
    hideReference?: boolean;
    columnDefinitions: Record<string, number>
}


const FindingTableRow = ({
    index,
    item: itemProp,
    context,
    hideStatus,
    hideTrial,
    hideInstitution,
    hideParticipant,
    hideType,
    hideEvent,
    hideForm,
    hideItemGroup,
    hideItem,
    hideReference,
    columnDefinitions
}: IVirtuosoTableInnerItem) => {
    const theme = useTheme();
    
    const createdDate = itemProp.createdDate ? DateTime.fromISO(itemProp.createdDate).toFormat('dd/MM/yyyy') : undefined;
    const resolvedDate = itemProp.resolvedDate ? DateTime.fromISO(itemProp.resolvedDate).toFormat('dd/MM/yyyy') : undefined;

    const status = itemProp.status;
    const statusText = sourceDataFindingStatusToText(status);

    const typeText = sourceDataFindingTypeToText(itemProp.type);
    
    const {
        trials,
        coordinatingGroups,
        masterGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData
    } = context;

    const trial = (itemProp.trialId ? trials?.[itemProp.trialId] : undefined);
    const masterGroup = (itemProp.masterGroupId ? masterGroups?.[itemProp.masterGroupId] : undefined);
    const coordinatingGroup = (itemProp.coordinatingGroupId ? coordinatingGroups?.[itemProp.coordinatingGroupId] : undefined);
    const institution = (itemProp.institutionId ? institutions?.[itemProp.institutionId] : undefined);
    const participant = (itemProp.participantId ? participants?.[itemProp.participantId] : undefined);

    const studyEvent = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid
    );

    const caseReportForm = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid,
        itemProp.caseReportFormOid
    );

    const itemGroup = useItemGroupConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid,
        itemProp.caseReportFormOid,
        itemProp.caseReportFormVersionOid,
        itemProp.itemGroupName
    );

    const item = useItem(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        sourceData ?? new SourceData({
            date: undefined,
            events: undefined,
            forms: undefined,
            items: {
                [itemProp.studyEventOid!]: {
                    [itemProp.studyEventRepeat!]: {
                        [itemProp.caseReportFormOid!]: {
                            [itemProp.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                [itemProp.itemGroupRepeat!]: {
                                    [itemProp.itemName!]: new SourceDataItem({
                                        value: undefined
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        itemProp.studyEventOid,
        itemProp.studyEventRepeat,
        itemProp.caseReportFormOid,
        itemProp.caseReportFormVersionOid,
        itemProp.itemGroupName,
        itemProp.itemGroupRepeat,
        itemProp.itemName
    )
    
    const contextStatus = useMemo(() => {
        if (itemProp.participantId) {
            return participants?.[itemProp.participantId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.institutionId) {
            return institutions?.[itemProp.institutionId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.coordinatingGroupId) {
            return coordinatingGroups?.[itemProp.coordinatingGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.masterGroupId) {
            return masterGroups?.[itemProp.masterGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.trialId) {
            return trials?.[itemProp.trialId]?.status ?? ConfigurationStatus.Disabled;
        }
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        itemProp
    ])

    return (
        <>
            {
                !hideStatus && 
                <TableCell sx={{width: columnDefinitions["Status"]}}>
                    <StatusListItemTooltip
                        title={
                            <>
                                <strong>Status</strong><br />
                                {statusText}
                            </>
                        }
                    >
                        <StatusChip label={statusText} color={sourceDataFindingStatusToColor(status)} />
                    </StatusListItemTooltip>
                </TableCell>
            }
            {
                !hideTrial &&
                <TableCell sx={{width: columnDefinitions["Trial"]}}><Tooltip title="Go to Trial" followCursor><Link to={`/${trial?.oid}`}>{trial?.name}</Link></Tooltip></TableCell>
            }
            {
                !hideInstitution && 
                <TableCell sx={{width: columnDefinitions["Institution"]}} ><Tooltip title="Go to Institution" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}`}>{institution?.name}</Link></Tooltip></TableCell>
            }
            {
                !hideParticipant && 
                <TableCell sx={{width: columnDefinitions["Participant"]}} ><Tooltip title="Go to Participant" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}`}>{participant?.studyNumber}</Link></Tooltip></TableCell>
            }
            {
                !hideEvent && 
                <TableCell sx={{width: columnDefinitions["Study Event"]}} >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Tooltip title="Go to Study Event" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}/events/${studyEvent?.studyEventOid}/${itemProp?.studyEventRepeat}`}>{studyEvent?.displayName}</Link></Tooltip>
                        {
                            studyEvent?.eventMetadata.type === SourceDataEventType.Repeating && (
                                <StatusListItemTooltip 
                                    title="Event Repeat"
                                    followCursor
                                >
                                    <Chip 
                                        size="small" 
                                        icon={<FontAwesomeIcon icon={AppSettings.icons.studyEventRepeat} style={{fontSize: '1em'}} />} 
                                        label={itemProp.studyEventRepeat} 
                                    />
                                </StatusListItemTooltip>
                            )
                        }
                    </Box>
                </TableCell>
            }
            {
                !hideForm && 
                <TableCell sx={{width: columnDefinitions["Case Report Form"]}} ><Tooltip title="Go to Case Report Form" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}/events/${studyEvent?.studyEventOid}/${itemProp?.studyEventRepeat}/forms/${caseReportForm?.caseReportFormOid}`}>{caseReportForm?.displayName}</Link></Tooltip></TableCell>
            }
            {
                !hideItemGroup && 
                <TableCell sx={{width: columnDefinitions["Item Group"]}} >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        {itemGroup?.displayName}
                        {
                            itemGroup?.itemGroupMetadata?.type === SourceDataItemGroupType.Grid && (
                                <StatusListItemTooltip 
                                    title="Item Group Repeat"
                                    followCursor
                                >
                                    <Chip 
                                        size="small" 
                                        icon={<FontAwesomeIcon icon={AppSettings.icons.itemGroupRepeat} style={{fontSize: '1em'}} />} 
                                        label={itemProp.itemGroupRepeat} 
                                    />
                                </StatusListItemTooltip>
                            )
                        }
                    </Box>
                </TableCell>
            }
            {
                !hideItem && 
                <TableCell sx={{width: columnDefinitions["Item"]}} >{item?.displayName}</TableCell>
            }
            {
                !hideType && 
                <TableCell sx={{width: columnDefinitions["Type"]}} >{typeText}</TableCell>
            }
            {
                !hideReference && 
                <TableCell sx={{width: columnDefinitions["Reference"]}} >{itemProp?.reference}</TableCell>
            }
            <TableCell sx={{width: columnDefinitions["Date Created"]}} >{createdDate}</TableCell>
            <StatusListItemTooltip
                title={
                    <>
                        <strong>Created By</strong><br />
                        {itemProp?.creator}<br />
                        {itemProp?.creatorUsername}
                    </>
                }
            >
                <TableCell sx={{width: columnDefinitions["Created By"]}} >
                    {itemProp?.creator}
                </TableCell>
            </StatusListItemTooltip>
            <TableCell sx={{width: columnDefinitions["Details"], whiteSpace: "pre-wrap"}} >{itemProp?.detail}</TableCell>
            <TableCell sx={{width: columnDefinitions["Discussed With"]}} >{itemProp?.discussedWith}</TableCell>
            <TableCell sx={{width: columnDefinitions["Date Resolved"]}} >{resolvedDate}</TableCell>
            <StatusListItemTooltip
                title={
                    <>
                        <strong>Resolved By</strong><br />
                        {itemProp?.creator}<br />
                        {itemProp?.creatorUsername}
                    </>
                }
            >
                <TableCell sx={{width: columnDefinitions["Resolved By"]}} >{itemProp?.resolver}</TableCell>
            </StatusListItemTooltip>
            <TableCell sx={{width: columnDefinitions["Resolution"], whiteSpace: "pre-wrap"}} >{itemProp?.resolution}</TableCell>
            <TableCell sx={{width: columnDefinitions["Actions"]}}>
                {
                    itemProp.status === SourceDataFindingStatus.Pending && contextStatus === ConfigurationStatus.Enabled && (

                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center"
                            }}
                        >
                            <Button
                                color="primary"
                                variant="contained"
                                component={Link}
                                to={`${location.pathname}/${itemProp.id}/resolve`}
                            >
                                Resolve
                            </Button>
                        </Box>
                    )
                }
            </TableCell>
        </>
    );
}

// Item contents are cached properly with React.memo
const InnerItem =({ 
    index,
    item,
    context
}: IVirtuosoInnerItem) => {
    return (
        <SourceDataFindingView
            {...context}
            data={item}
        />
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;
const MemoizedFindingTableRow = memo(FindingTableRow) as typeof FindingTableRow;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: SourceDataFinding, context: IVirtuosoContext) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}

interface ISourceDataFindingsListProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    
    sourceDataFindings?: SourceDataFinding[];

    canAdd?: boolean;
    hideDetails?: boolean;
}

interface ISourceDataFindingsSearch extends Partial<Omit<SourceDataFinding, 'details' | typeof immerable>> {
    username?: string;
}

const useColumnDefinitions = () => {
    const theme = useTheme();

    const statusSpacing = theme.spacing(20);
    const trialSpacing = theme.spacing(20);
    const institutionSpacing = theme.spacing(30);
    const participantSpacing = theme.spacing(12);
    const studyEventSpacing = theme.spacing(30);
    const caseReportFormSpacing = theme.spacing(30);
    const itemGroupSpacing = theme.spacing(30);
    const itemSpacing = theme.spacing(50);
    const typeSpacing = theme.spacing(20);
    const referenceSpacing = theme.spacing(30);
    const dateCreatedSpacing = theme.spacing(25);
    const createdBySpacing = theme.spacing(20);
    const detailsSpacing = theme.spacing(75);
    const discussedWithSpacing = theme.spacing(40);
    const dateResolvedSpacing = theme.spacing(30);
    const resolvedBySpacing = theme.spacing(20);
    const resolutionSpacing = theme.spacing(75);
    const actionsSpacing = theme.spacing(10);

    const columnDefinitions: Record<string, number> = useMemo(() => ({
        "Status": +(statusSpacing.replace("px", "")),
        "Trial": +(trialSpacing.replace("px", "")),
        "Institution": +(institutionSpacing.replace("px", "")),
        "Participant": +(participantSpacing.replace("px", "")),
        "Study Event": +(studyEventSpacing.replace("px", "")),
        "Case Report Form": +(caseReportFormSpacing.replace("px", "")),
        "Item Group": +(itemGroupSpacing.replace("px", "")),
        "Item": +(itemSpacing.replace("px", "")),
        "Type": +(typeSpacing.replace("px", "")),
        "Reference": +(referenceSpacing.replace("px", "")),
        "Date Created": +(dateCreatedSpacing.replace("px", "")),
        "Created By": +(createdBySpacing.replace("px", "")),
        "Details": +(detailsSpacing.replace("px", "")),
        "Discussed With": +(discussedWithSpacing.replace("px", "")),
        "Date Resolved": +(dateResolvedSpacing.replace("px", "")),
        "Resolved By": +(resolvedBySpacing.replace("px", "")),
        "Resolution": +(resolutionSpacing.replace("px", "")),
        "Actions": +(actionsSpacing.replace("px", "")),
    }), [
        statusSpacing,
        trialSpacing,
        institutionSpacing,
        participantSpacing,
        typeSpacing,
        referenceSpacing,
        dateCreatedSpacing,
        createdBySpacing,
        detailsSpacing,
        dateResolvedSpacing,
        resolvedBySpacing,
        discussedWithSpacing,
        resolutionSpacing,
        actionsSpacing,
    ]);

    return columnDefinitions;
}


interface IDetailsProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
}

const Details: FC<IDetailsProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    trials,
    masterGroups,
    coordinatingGroups,
    institutions,
    participants,
    metadata,
    trialConfiguration,
    configuration,
    sourceData
}) => {
    return (
        <>
            <Subtitle variant="h2">
                Details
            </Subtitle>
            <Paper>
                <SourceDataFindingDetails 
                    trialId={trialId}
                    masterGroupId={masterGroupId}
                    coordinatingGroupId={coordinatingGroupId}
                    institutionId={institutionId}
                    participantId={participantId}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={studyEventRepeat}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat}
                    itemName={itemName}

                    trials={trials}
                    masterGroups={masterGroups}
                    coordinatingGroups={coordinatingGroups}
                    institutions={institutions}
                    participants={participants}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                    enableLinks={true}
                />
            </Paper>
        </>
    )
}

const SourceDataFindingsList: FC<ISourceDataFindingsListProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    sourceDataFindings,

    canAdd,
    hideDetails
}) => {
    const theme = useTheme();
    const userContext = useContext(ExternalUsersContext);
    const { idMap: trials } = useContext(TrialsContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const [urlSearchParams, setUrlSearchParams] = useSearchParams();

    const searchData = useMemo(() => {
        const search: ISourceDataFindingsSearch = {
            
        };

        if (!trialId && urlSearchParams.has('trialId')) {
            const query = urlSearchParams.get('trialId');

            search.trialId = query ? query as unknown as number : undefined;
        }

        if (!masterGroupId && urlSearchParams.has('masterGroupId')) {
            const query = urlSearchParams.get('masterGroupId');

            search.masterGroupId = query ? query as unknown as number : undefined;
        }

        if (!coordinatingGroupId && urlSearchParams.has('coordinatingGroupId')) {
            const query = urlSearchParams.get('coordinatingGroupId');

            search.coordinatingGroupId = query ? query as unknown as number : undefined;
        }

        if (!institutionId && urlSearchParams.has('institutionId')) {
            const query = urlSearchParams.get('institutionId');

            search.institutionId = query ? query as unknown as number : undefined;
        }

        if (!participantId && urlSearchParams.has('participantId')) {
            const query = urlSearchParams.get('participantId');

            search.participantId = query ? query as unknown as number : undefined;
        }

        if (!studyEventOid && urlSearchParams.has('studyEventOid')) {
            const query = urlSearchParams.get('studyEventOid');

            search.studyEventOid = query ? query : undefined;
        }

        if (!studyEventRepeat && urlSearchParams.has('studyEventRepeat')) {
            const query = urlSearchParams.get('studyEventRepeat');

            search.studyEventRepeat = query ? +query : undefined;
        }

        if (!caseReportFormOid && urlSearchParams.has('caseReportFormOid')) {
            const query = urlSearchParams.get('caseReportFormOid');

            search.caseReportFormOid = query ? query : undefined;
        }

        if (!caseReportFormVersionOid && urlSearchParams.has('caseReportFormVersionOid')) {
            const query = urlSearchParams.get('caseReportFormVersionOid');

            search.caseReportFormVersionOid = query ? query : undefined;
        }

        if (!itemGroupName && urlSearchParams.has('itemGroupName')) {
            const query = urlSearchParams.get('itemGroupName');

            search.masterGroupId = query ? +query : undefined;
        }

        if (!itemGroupRepeat && urlSearchParams.has('itemGroupRepeat')) {
            const query = urlSearchParams.get('itemGroupRepeat');

            search.itemGroupRepeat = query ? +query : undefined;
        }

        if (!itemName && urlSearchParams.has('itemName')) {
            const query = urlSearchParams.get('itemName');

            search.itemName = query ? query : undefined;
        }

        

        if (urlSearchParams.has('type')) {
            const query = urlSearchParams.get('type');

            search.type = query ? +query : undefined;
        }

        
        if (urlSearchParams.has('status')) {
            const query = urlSearchParams.get('status');

            search.status = query ? +query : undefined;
        }

        if(urlSearchParams.has('username')) {
            const query = urlSearchParams.get('username');

            search.username = query ?? undefined;
        }

        return search;
    }, 
    [
        urlSearchParams,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    ])

    const virtuosoContext = useMemo(() => {
        return {
            theme,
            trialId: trialId ?? searchData.trialId,
            masterGroupsId: masterGroupId ?? searchData.masterGroupId,
            coordinatingGroupId: coordinatingGroupId ?? searchData.coordinatingGroupId,
            institutionId: institutionId ?? searchData.institutionId,
            participantId: participantId ?? searchData.participantId,
            studyEventOid: studyEventOid ?? searchData.studyEventOid,
            studyEventRepeat: studyEventRepeat ?? searchData.studyEventRepeat,
            caseReportFormOid: caseReportFormOid ?? searchData.caseReportFormOid,
            caseReportFormVersionOid: caseReportFormVersionOid ?? searchData.caseReportFormVersionOid,
            itemGroupName: itemGroupName ?? searchData.itemGroupName,
            itemGroupRepeat: itemGroupRepeat ?? searchData.itemGroupRepeat,
            itemName: itemName ?? searchData.itemName,

            trials: trials,
            masterGroups,
            coordinatingGroups,
            institutions,
            participants,
            metadata,
            trialConfiguration,
            configuration,
            sourceData,
        }
    }, [
        theme,
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const searchableFields = useMemo(() => {
    //     const context = {
    //         trials,
    //         masterGroups,
    //         coordinatingGroups,
    //         institutions,
    //         participants
    //     }

    //     return [
    //         !trialId && trials ? DefaultSearchableFields.trial(context) : undefined,
    //         !masterGroupId && masterGroups ? DefaultSearchableFields.masterGroup(context) : undefined,
    //         !coordinatingGroupId && coordinatingGroups ? DefaultSearchableFields.coordinatingGroup(context) : undefined,
    //         !institutionId && institutions ? DefaultSearchableFields.institution(context) : undefined,
    //         !participantId && participants ? DefaultSearchableFields.participant(context) : undefined,

    //         DefaultSearchableFields.type(context),
    //         DefaultSearchableFields.status(context)
    //     ].filter(x => !!x) as ISearchField<any>[]
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId
    // ]);

    const sortableFields = useMemo(() => {
        return [
            // !trialId && trials && !searchData.trialId ? DefaultSortableFields.trial : undefined,
            // !masterGroupId && masterGroups && !searchData.masterGroupId ? DefaultSortableFields.masterGroup : undefined,
            // !coordinatingGroupId && coordinatingGroups && !searchData.coordinatingGroupId ? DefaultSortableFields.coordinatingGroup : undefined,
            // !institutionId && institutions && !searchData.institutionId ? DefaultSortableFields.institution : undefined,
            // !participantId && participants && !searchData.participantId ? DefaultSortableFields.participant : undefined,

            DefaultSortableFields.type,
            DefaultSortableFields.status
        ].filter(x => !!x) as ISourceDataFindingsListSortable[]
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const [searchOpen, setSearchOpen] = useState(false);
    const [selectedSort, setSelectedSort] = useState<undefined | {field: ISourceDataFindingsListSortable; direction: 'asc' | 'desc'}>(undefined);

    const onSortChange = useCallback((sort : {field: ISourceDataFindingsListSortable; direction: 'asc' | 'desc'}) => {
        setSelectedSort(sort)
    }, [setSelectedSort])
    
    // const onSearchClose = useCallback(() => {
    //     setSearchOpen(false);
    // }, [setSearchOpen])

    // const onSearchOk = useCallback(async (data?: ISearchForm) => {
    //     const newSearch = {...data};

    //     if (newSearch.participantId) {
    //         const participant = participants?.[newSearch.participantId];

    //         newSearch.trialId = participant?.trialId;
    //         newSearch.masterGroupId = participant?.masterGroupId;
    //         newSearch.coordinatingGroupId = participant?.coordinatingGroupId;
    //         newSearch.institutionId = participant?.institutionId;
    //     }
    //     else if (newSearch.institutionId) {
    //         const institution = institutions?.[newSearch.institutionId];

    //         newSearch.trialId = institution?.trialId;
    //         newSearch.masterGroupId = institution?.masterGroupId;
    //         newSearch.coordinatingGroupId = institution?.coordinatingGroupId;
    //     }
    //     else if (newSearch.coordinatingGroupId) {
    //         const coordinatingGroup = coordinatingGroups?.[newSearch.coordinatingGroupId];

    //         newSearch.trialId = coordinatingGroup?.trialId;
    //         newSearch.masterGroupId = coordinatingGroup?.masterGroupId;
    //     }
    //     else if (newSearch.masterGroupId) {
    //         const masterGroup = masterGroups?.[newSearch.masterGroupId];

    //         newSearch.trialId = masterGroup?.trialId;
    //     }

    //     for (let key in newSearch) {
    //         if (newSearch[key] === undefined) {
    //             delete newSearch[key];
    //         }
    //     }

    //     setUrlSearchParams(newSearch as any)
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId,
        
    //     setUrlSearchParams
    // ])


    // const filterCount = useMemo(() => {
    //     const searchedFields = searchableFields?.filter(x => !!searchData?.[x.key as keyof ISourceDataFindingsSearch]) ?? [];

    //     return searchedFields.length;
    // }, [searchData, searchableFields]);

    const filteredUserContext = useMemo(() => {
        const distinctUsernames = sourceDataFindings ?
            Array.from(new Set(sourceDataFindings?.map(finding => finding.creatorUsername))) :
            [];

        return {
            ...userContext,
            data: userContext.data?.filter(user => distinctUsernames.includes(user.username)) ?? []
        }
    }, [sourceDataFindings, userContext]);

    const sortedListItems = useMemo(() => {

        let newData = sourceDataFindings?.filter(x => {
            for (let key in searchData) {
                if(key as keyof ISourceDataFindingsSearch === "username") {
                    if(x?.creatorUsername !== searchData.username) {
                        return false;
                    }
                }
                else if (
                    !!searchData[key as keyof ISourceDataFindingsSearch] && 
                    searchData[key as keyof ISourceDataFindingsSearch] != x[key as keyof SourceDataFinding]
                ) {
                    return false;
                }
            }

            return true;
        }) ?? [];

        if (!!selectedSort?.field  && !!selectedSort.direction && newData) {
            const context = {
                trials,
                masterGroups,
                coordinatingGroups,
                institutions,
                participants
            }

            newData = newData.sort((a,b) => selectedSort.field.sort(a,b,context) * (selectedSort.direction === 'desc' ? -1 : 1));
        }

        return newData;
    }, 
    [
        sourceDataFindings, 
        searchData, 
        selectedSort,
        
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants
    ]);

    const [viewType, setViewType] = useLocalStorage<ListViewType>('findings-view-type', ListViewType.List);

    const location = useLocation();

    const hasSearch = true;
    const hasSort = true;

    const [scroller, setScroller] = useState<HTMLElement | null>(null);

    const hideTrial = useMemo(() => !!trialId || !!searchData.trialId, [searchData.trialId, trialId]);
    const hideInstitution = useMemo(() => !!institutionId || !!searchData.institutionId, [searchData.institutionId, institutionId]);
    const hideParticipant = useMemo(() => !!participantId || !!searchData.participantId, [searchData.participantId, participantId]);
    const hideEvent = useMemo(() => !!studyEventOid || !!searchData.studyEventOid, [searchData.studyEventOid, studyEventOid]);
    const hideForm = useMemo(() => !!searchData.caseReportFormOid, [searchData.caseReportFormOid]);
    const hideItemGroup = useMemo(() => !!searchData.itemGroupName, [searchData.itemGroupName]);
    const hideItem = useMemo(() => !!searchData.itemName, [searchData.itemName]);
    const hideType = useMemo(() =>  !!searchData.type, [searchData.type]);
    const hideReference = useMemo(() => !!searchData.reference, [searchData.reference]);
    const hideStatus = useMemo(() => !!searchData.status, [searchData.status]);

    const columnDefinitions = useColumnDefinitions();

    const tableComponents = useMemo(() => ({
        Table: FindingTable,// (props: any) => <Table {...props} sx={{tableLayout: "fixed"}} />,
        TableHead: (props: any) => (
            <TableHead 
                {...props} 
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#121212" : theme.palette.common.white,
                    backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))"
                }}
            />
        ),
        TableRow: StyledTableRow
    }), [theme.palette.mode]);
    
    const columnNames = useMemo(() => {
        const columnNames: string[] = [];

        if(!hideStatus) {
            columnNames.push("Status");
        }
    
        if(!hideTrial) {
            columnNames.push("Trial");
        }
    
        if(!hideInstitution) {
            columnNames.push("Institution");
        }
    
        if(!hideParticipant) {
            columnNames.push("Participant");
        }
    
        if(!hideEvent) {
            columnNames.push("Study Event");
        }
    
        if(!hideForm) {
            columnNames.push("Case Report Form");
        }
    
        if(!hideItemGroup) {
            columnNames.push("Item Group");
        }
    
        if(!hideItem) {
            columnNames.push("Item");
        }
    
        if(!hideType) {
            columnNames.push("Type");
        }
    
        if(!hideReference) {
            columnNames.push("Reference");
        }
        
        columnNames.push("Date Created");
        columnNames.push("Created By");

        columnNames.push("Details");
        columnNames.push("Discussed With");
        columnNames.push("Date Resolved");
        columnNames.push("Resolved By");
        columnNames.push("Resolution");

        columnNames.push("Actions");

        return columnNames;
    }, [hideStatus, hideTrial, hideInstitution, hideParticipant, hideEvent, hideForm, hideItemGroup, hideItem, hideType, hideReference]);
    
        
    const virtuosoTableRow = useCallback((index: number, item: SourceDataFinding, context: IVirtuosoContext) => {
        return (
            <MemoizedFindingTableRow 
                index={index} 
                item={item} 
                context={context} 
                hideTrial={hideTrial}
                hideInstitution={hideInstitution}
                hideParticipant={hideParticipant}
                hideEvent={hideEvent}
                hideForm={hideForm}
                hideItemGroup={hideItemGroup}
                hideItem={hideItem}
                hideStatus={hideStatus}
                hideType={hideType}
                hideReference={hideReference}
                columnDefinitions={columnDefinitions}
            />
        );
    }, [hideStatus, hideTrial, hideInstitution, hideParticipant, hideType, hideReference, hideEvent, hideForm, hideItemGroup, hideItem, columnDefinitions]);
    
    const totalWidth = useMemo(() => {
        return columnNames.reduce<number>((state, current) => state+columnDefinitions[current], 0)
    }, [columnNames, columnDefinitions]);

    return (
        <ExternalUsersContext.Provider value={filteredUserContext}>
            {
                !hideDetails && (
                    <Details
                        trialId={trialId ?? searchData.trialId}
                        masterGroupId={masterGroupId ?? searchData.masterGroupId}
                        coordinatingGroupId={coordinatingGroupId ?? searchData.coordinatingGroupId}
                        institutionId={institutionId ?? searchData.institutionId}
                        participantId={participantId ?? searchData.participantId}
                        studyEventOid={studyEventOid ?? searchData.studyEventOid}
                        studyEventRepeat={studyEventRepeat ?? searchData.studyEventRepeat}
                        caseReportFormOid={caseReportFormOid ?? searchData.caseReportFormOid}
                        caseReportFormVersionOid={caseReportFormVersionOid ?? searchData.caseReportFormVersionOid}
                        itemGroupName={itemGroupName ?? searchData.itemGroupName}
                        itemGroupRepeat={itemGroupRepeat ?? searchData.itemGroupRepeat}
                        itemName={itemName ?? searchData.itemName}
        
                        trials={trials}
                        masterGroups={masterGroups}
                        coordinatingGroups={coordinatingGroups}
                        institutions={institutions}
                        participants={participants}
                        metadata={metadata}
                        trialConfiguration={trialConfiguration}
                        configuration={configuration}
                        sourceData={sourceData}
                    />
                )
            }     
            <Subtitle variant="h2">
                Findings
            </Subtitle>
            <FilterSortBox>
                <CountBox>
                    {sortedListItems.length} Items
                </CountBox>
                <SortBox>
                    <SourceDataFindingsListFilter
                        onChange={(type, status, username) => {
                            let data: any = {};

                            if (type) {
                                data.type = type;
                            }
                            
                            if (status) {
                                data.status = status;
                            }

                            if(username) {
                                data.username = username;
                            }

                            setUrlSearchParams(data)
                        }}
                        selectedStatus={searchData.status}
                        selectedType={searchData.type}
                        selectedUsername={searchData.username}
                    />
                </SortBox>
                {/* {
                    hasSearch && (
                        <SearchBox>
                            <Tooltip title="Filter">
                                <SearchButton onClick={() => setSearchOpen(true)} color={!!filterCount ? "primary" : "inherit"} variant="contained">
                                    <FontAwesomeIcon icon={AppSettings.icons.filter} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                } */}
                {
                    hasSort && (
                        <SortBox>
                            <SourceDataFindingsListSort 
                                sortableFields={sortableFields}
                                sortKey="sourceDataFindings-and-queries"
                                onChange={onSortChange}
                            />
                        </SortBox>
                    )
                }
                <SortBox>
                    <ListTableToggle 
                        onChange={(val: ListViewType) => setViewType(val)} 
                        value={viewType ?? ListViewType.List}
                    />
                </SortBox>
                {
                    canAdd && (
                        <SearchBox sx={{ml:2}}>
                            <Tooltip title="Add">
                                <SearchButton 
                                    variant="contained" 
                                    color="primary"
                                    //@ts-ignore
                                    component={Link}
                                    to={`${location.pathname}/add`}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                }
            </FilterSortBox>

            {
                sortedListItems.length === 0 && (
                    <Alert
                        severity="warning"
                    >
                        <AlertTitle>No Findings</AlertTitle>
                        No findings were found that match the requested criteria.
                    </Alert> 
                )
            }
            {
                sortedListItems.length !== 0 && viewType === ListViewType.List &&
                <List disablePadding>
                    {
                        sortedListItems.length > 20 && (
                            <Virtuoso 
                                useWindowScroll
                                data={sortedListItems}
                                context={virtuosoContext}
                                itemContent={virtuosoItemContent}
                            />  
                        )
                    }
                    {
                        sortedListItems.length <= 20 && (
                            sortedListItems.map((x, i) => (
                                <MemoizedInnerItem key={i} index={i} item={x} context={virtuosoContext} />
                            ))
                        )
                    }              
                </List>
            }
            {
                sortedListItems.length !== 0 && viewType === ListViewType.Table && (
                    <Paper 
                        ref={(ref) => setScroller(ref as HTMLElement)}
                        sx={{
                            height: `calc(100vh - ${theme.spacing(14)})`,
                            overflow: "auto"
                        }}
                    >
                        <Box
                            sx={{
                                width: totalWidth
                            }}
                        >
                            {
                                scroller &&
                                sortedListItems.length > 0 &&
                                    <TableVirtuoso
                                        components={tableComponents}
                                        fixedHeaderContent={() => ( 
                                            <TableRow>
                                            {
                                                columnNames.map(cn => (
                                                    <TableCell key={`table-header-${cn.replace(" ", "-")}`} sx={{width: columnDefinitions[cn]}} >{cn}</TableCell>
                                                ))
                                            }
                                            </TableRow>
                                        )}
                                        useWindowScroll
                                        customScrollParent={scroller}
                                        data={sortedListItems}
                                        context={virtuosoContext}
                                        itemContent={virtuosoTableRow}
                                    />
                            }
                        </Box>
                    </Paper>
                )
            }
            {/* <SearchDialog 
                open={searchOpen}
                searchFields={searchableFields}
                formData={searchData}
                onSave={onSearchOk}
                onClose={onSearchClose}
            /> */}
        </ExternalUsersContext.Provider>
    )
}

export default SourceDataFindingsList;